/**
 * A PDF made of JPEG pages - the "scan" a photographed receipt becomes. JPEG data goes in as is
 * (DCTDecode), so nothing is re-encoded and the file stays small. One page per photo, sized so
 * the image prints at `dpi`: a tall, narrow receipt gives a tall, narrow page.
 *
 * Shared by the phone (lib/scan/photo.ts) and services/ai (lib/documents.ts), so no Node APIs.
 */
export type PdfPage = { jpeg: Uint8Array; width: number; height: number; gray: boolean };

export function jpegPdf(pages: PdfPage[], dpi = 200): Uint8Array<ArrayBuffer> {
  if (pages.length === 0) throw new Error("A PDF needs at least one page");
  const chunks: Uint8Array[] = [];
  const offsets: number[] = [];
  let length = 0;
  const write = (chunk: string | Uint8Array) => {
    // PDF syntax here is ASCII plus the four latin1 bytes of the binary marker.
    const bytes =
      typeof chunk === "string" ? Uint8Array.from(chunk, (char) => char.charCodeAt(0)) : chunk;
    chunks.push(bytes);
    length += bytes.length;
  };
  const object = (id: number, body: () => void) => {
    offsets[id] = length;
    write(`${id} 0 obj\n`);
    body();
    write("\nendobj\n");
  };

  // Objects: 1 catalog, 2 page tree, then per page: page, content stream, image.
  const pageIds = pages.map((_, index) => 3 + index * 3);
  write("%PDF-1.4\n%\xE2\xE3\xCF\xD3\n");
  object(1, () => write("<< /Type /Catalog /Pages 2 0 R >>"));
  object(2, () =>
    write(
      `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pages.length} >>`,
    ),
  );
  pages.forEach((page, index) => {
    const pageId = pageIds[index]!;
    const pointsW = ((page.width * 72) / dpi).toFixed(2);
    const pointsH = ((page.height * 72) / dpi).toFixed(2);
    const content = `q ${pointsW} 0 0 ${pointsH} 0 0 cm /Im0 Do Q`;
    object(pageId, () =>
      write(
        `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pointsW} ${pointsH}] ` +
          `/Resources << /XObject << /Im0 ${pageId + 2} 0 R >> >> /Contents ${pageId + 1} 0 R >>`,
      ),
    );
    object(pageId + 1, () =>
      write(`<< /Length ${content.length} >>\nstream\n${content}\nendstream`),
    );
    object(pageId + 2, () => {
      write(
        `<< /Type /XObject /Subtype /Image /Width ${page.width} /Height ${page.height} ` +
          `/ColorSpace /${page.gray ? "DeviceGray" : "DeviceRGB"} /BitsPerComponent 8 ` +
          `/Filter /DCTDecode /Length ${page.jpeg.length} >>\nstream\n`,
      );
      write(page.jpeg);
      write("\nendstream");
    });
  });

  const objectCount = 2 + pages.length * 3;
  const xref = length;
  write(`xref\n0 ${objectCount + 1}\n0000000000 65535 f \n`);
  for (let id = 1; id <= objectCount; id += 1)
    write(`${String(offsets[id]).padStart(10, "0")} 00000 n \n`);
  write(`trailer\n<< /Size ${objectCount + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`);
  const pdf = new Uint8Array(length);
  let at = 0;
  for (const chunk of chunks) {
    pdf.set(chunk, at);
    at += chunk.length;
  }
  return pdf;
}
